import React, { useState, useRef, useEffect } from 'react';
import { Send, X, Minus, Maximize2, Menu, Download, Info, ChevronUp, ChevronDown, Lightbulb, FileText, Users, Building2, Award, Cpu, Handshake, ExternalLink, PanelLeftClose, PanelLeftOpen, Calendar, Copy, Check, ThumbsUp, ThumbsDown, Mic, MicOff, ShieldCheck } from 'lucide-react';

const SAMPLE_QUESTIONS = [
  "What have you built on Azure AI and Copilot Studio?",
  "What does working with ThirdEye Data look like?",
  "How do you stop AI projects stalling before production?",
  "What AI agents have you actually put into production?",
  "How have you used AI for automated inventory counting in smart warehousing?",
];

const CANNED_ANSWERS = {
  "what computer vision problems have you solved for enterprises?": `We've successfully addressed several computer vision challenges for enterprises, enhancing their operational efficiency and automating visual tasks. Here are some key problems we've solved:

* **Manual Visual Inspection**: Many organizations face the inefficiencies of manual inspections of images and videos. We automate this process, ensuring consistent quality while reducing operational costs — see our [Defects Detection System for Alloy Wheel Manufacturer](https://thirdeyedata.ai/case-studies/defects-detection-system-for-alloy-wheel-manufacturer).

* **Automated Product & Inventory Counting**: We've deployed computer vision systems that automatically count and track components and stock in real time, removing manual counting errors — see our [AI Counting Solution: Case Study for Connector Manufacturers](https://thirdeyedata.ai/case-studies/ai-based-product-counting-solution-for-electronic-connector-manufacturer/) and [Case Study: AI Solution for FMCG Product Counting](https://thirdeyedata.ai/case-studies/ai-based-product-counting-solution-for-a-fmcg-company/).

* **Visual Document Processing**: Enterprises handle vast amounts of documents that need processing and analysis. Our computer vision applications convert unstructured visual data into structured information, enabling actionable insights — see our [Generative AI-Powered Document Analytics and Data Extraction Platform](https://thirdeyedata.ai/case-studies/generative-ai-powered-document-analytics-and-data-extraction-platform).

* **Safety and Surveillance**: Companies in sectors like manufacturing and logistics rely on visual monitoring for safety compliance. We develop robust systems that enhance surveillance capabilities, providing real-time insights and alerts — see our [AI-Driven HSE Non-Compliance Detection for Hazard Prevention](https://thirdeyedata.ai/case-studies/ai-driven-hse-non-compliance-detection-for-hazard-prevention).

* **Integrating Vision into Business Workflows**: We embed computer vision directly into existing enterprise systems. This integration ensures that insights lead to immediate actions, maintaining operational agility — see our [Transforming Manufacturing Operations with AI](https://thirdeyedata.ai/case-studies/transforming-manufacturing-operations-with-ai).

For a deeper dive into our computer vision solutions, check out our dedicated page: [Computer Vision Solutions](https://thirdeyedata.ai/computer-vision-intelligence).

✨ You might also want to know:
- [Detecting Anomalies in Images](https://thirdeyedata.ai/detecting-anomalies-in-images-using-computer-vision-based-regression-models/)
- [15+ Computer Vision Applications](https://thirdeyedata.ai/computer-vision-applications/)
- [Consulting & Implementation Services](https://thirdeyedata.ai/consulting-implementation-services/low-code-no-code-development/)
- [Case Studies Across Various Industries](https://thirdeyedata.ai/case-studies)

💬 You might also ask:
- How long does a computer vision pilot typically take?
- Can this integrate with our existing camera systems?
- What industries have you deployed computer vision in?`,
  "can you work inside our existing microsoft stack?": `Yes — Microsoft is one of our core technology partners, and a large share of our AI and data engineering work is built natively inside the Microsoft ecosystem rather than bolted on beside it. We regularly work with:

* Azure AI & Azure Machine Learning for model training, deployment, and MLOps
* Microsoft Fabric and Azure Databricks for data engineering and analytics pipelines
* Copilot Studio and Azure OpenAI Service for building AI assistants and agents
* Power BI for reporting and business intelligence

Because we build directly on top of your existing Microsoft investment, integration is typically faster and lower-risk than introducing a separate, unfamiliar stack.

Learn more about our relationship with Microsoft here: [ThirdEye Data's Long-term Partnership with Microsoft](https://thirdeyedata.ai/partnerships/microsoft).

🤔 Want to dive deeper?
- Do you also work with AWS or Google Cloud?
- How do you handle data security within Azure?
- What does a typical Microsoft-based engagement look like?`,
  "can we start from something you've already built rather than from scratch?": `Yes — that's actually how most of our engagements start. We maintain a library of 20+ pre-built, production-ready AI solutions that we adapt to your data and workflows, rather than building everything from a blank page. A few examples:

* [Automated Stock Counting System](https://thirdeyedata.ai/ai-demo-solutions/automated-stock-counting-system) — computer vision-based inventory counting, ready to adapt to your warehouse setup.
* [AI-based Food Quality Inspection System](https://thirdeyedata.ai/ai-demo-solutions/ai-based-food-quality-inspection-system) — automated visual quality checks for food and manufacturing lines.
* [Intelligent Document Automation Platform](https://thirdeyedata.ai/ai-demo-solutions/intelligent-document-automation-platform) — extracts and processes information from documents automatically.
* [Cash Flow AI Agents](https://thirdeyedata.ai/ai-demo-solutions/cash-flow-ai-agents) — AI agents that monitor and forecast cash flow in real time.
* [Care Roster AI](https://thirdeyedata.ai/ai-demo-solutions/care-roster-ai) — automates shift and staff scheduling for healthcare and service teams.

Starting from one of these means faster time-to-value and lower risk than a from-scratch build, since the core engineering is already proven — we tailor it to your specific data, systems, and requirements.

Explore the full library here: [ThirdEye Data's AI Demo Central - 20+ Pre-built AI Solutions](https://thirdeyedata.ai/demo-central/).

🤔 Want to dive deeper?
- How much customization is typically needed?
- What's the typical timeline to go live?
- Can I see a live demo before we commit?`,
  "what ai agents have you actually put into production?": `We've successfully put several AI agents into production that demonstrate our capability in automating complex processes and enhancing operational efficiency. Here are some noteworthy implementations:

* **Multi-Agent System for Customer Loyalty Programs**: We developed a system that transforms how loyalty programs are managed for a leading marketing company, enabling seamless customer interactions and data management. [Read more about this project](https://thirdeyedata.ai/case-studies/agentic-ai-solution-for-customer-loyalty-program/).

* **Multi-Agent Investment Research Tool**: This Copilot-based assistant automates the entire process of investment discovery, data collection, analysis, and reporting, significantly enhancing the efficiency of financial analysts. [Check out the case study here](https://thirdeyedata.ai/case-studies/multi-agent-investment-research-tool/).

* **AI-Powered Support Ticket Analysis & Workflow Automation**: We built an agentic system that automatically analyzes incoming support tickets and triggers the right workflow actions, cutting manual triage time for support teams. [See the case study here](https://thirdeyedata.ai/case-studies/ai-powered-support-ticket-analysis-workflow-automation-system).

Our experience with these AI agents highlights our commitment to developing solutions that work in real-world environments, ensuring that they meet enterprise constraints and deliver tangible value.

✨ You might also want to know:
- [Generative AI solutions](https://thirdeyedata.ai/generative-ai)
- [Data Engineering services](https://thirdeyedata.ai/data-engineering)
- [Computer Vision Intelligence](https://thirdeyedata.ai/computer-vision-intelligence)
- [AI Demo Solutions](https://thirdeyedata.ai/ai-demo-solutions)

💬 You might also ask:
- How do you keep AI agents within enterprise guardrails?
- What does your agent development process look like?
- Can these agents integrate with our existing systems?`,
};
// Quick Step Links — each row is a single clickable link that opens in a new tab.
const QUICK_STEP_LINKS = [
  {
    label: 'Schedule a Meeting with Us',
    url: 'https://thirdeyedata.ai/contact-us/',
    icon: Calendar,
    color: '#0ea5e9', // sky blue
  },
  {
    label: 'Download Top Project References',
    url: 'https://app-na2.hubspotdocuments.com/documents/6848428/view/1806807747?accessId=f7b9e8',
    icon: Download,
    color: '#0ea5e9', // sky blue
  },
  {
    label: 'Explore 20+ Pre-built AI Solutions',
    url: 'https://thirdeyedata.ai/demo-central/',
    icon: Cpu,
    color: '#0ea5e9', // sky blue
  },
];

// ─── FIXED MessageContent ─────────────────────────────────────────────────────
const MessageContent = ({ text, onFollowUp, isBot = false, streaming = false }) => {
  /*
  Description: A React component that renders the content of a single chat message, formatting text, links, lists, and follow-up buttons.
  Input: { text (string), onFollowUp (function) } - The message text and a callback for follow-up button clicks.
  Output: Returns a rendered JSX element containing the formatted message.
  */
  const cleanXMLTags = (content) => {
    /*
    Description: Removes raw XML/HTML tags (like <page_content>, <metadata>, <source>) from the generated message text to display clean content.
    Input: content (string) - The raw text string potentially containing XML tags.
    Output: Returns the cleaned text string (string).
    */
    if (!content) return '';
    return content
      .replace(/<page_content[^>]*>/gi, '')
      .replace(/<\/page_content>/gi, '')
      .replace(/<metadata[^>]*>[\s\S]*?<\/metadata>/gi, '')
      .replace(/<source[^>]*>/gi, '')
      .replace(/<\/source>/gi, '')
      .replace(/<url[^>]*>/gi, '')
      .replace(/<\/url>/gi, '');
  };

  // Split response into main content + explore links (LLM-generated) + ask questions (LLM-generated plain text)
  const parseFollowUps = (content) => {
    const sectionMarkers = [
      // Emoji-prefixed markers are tried first so the split point sits BEFORE the emoji --
      // otherwise the emoji is left dangling at the end of mainContent (rendered as a stray
      // sparkle on its own line). The text-only marker stays last, as a fallback for when an
      // upstream emoji was encoded incorrectly and never matches one of these.
      /🔗\s*Explore:/i,
      /[✨🤔👉💡]\s*(We can also look at|You might find these interesting|Want to dive deeper\??|I can also help you with|You might also like|You might also want to know)[^\n]*/i,
      /\*\*(We can also look at|You might find these interesting|Want to dive deeper\??|I can also help you with)\*\*/i,
      /[✨🤔👉💡🔍]\s*[A-Z][^\n]{0,60}:/,
      /\b(You might also ask|Want to dive deeper|We can also look at|You might find these interesting|I can also help you with|You might also like|You might also want to know)\b[^\n]*/i,
    ];

    let mainContent = content;
    let exploreLinks = [];
    let askQuestions = [];

    let sectionStart = -1;
    let sectionMarkerLen = 0;
    for (const marker of sectionMarkers) {
      const m = content.match(marker);
      if (m) {
        sectionStart = content.indexOf(m[0]);
        sectionMarkerLen = m[0].length;
        break;
      }
    }

    if (sectionStart === -1) return { mainContent, exploreLinks, askQuestions };

    mainContent = content.substring(0, sectionStart).trim();
    // Strip any leftover follow-up subheading lines that leaked into mainContent
    mainContent = mainContent.replace(/[✨🤔👉💡🔗💬]\s*(You might also|We can also|Want to dive|I can also|You might find|Ask me)[^\n]*/gi, '').trim();
    const afterMarker = content.substring(sectionStart + sectionMarkerLen);

    const bulletRegex = /^[\s]*[•\-\*]\s*(.+)$/gm;
    const GENERIC = new Set(['here','this','link','click here','click','read more','learn more','see more','more','website','page']);
    let m;
    while ((m = bulletRegex.exec(afterMarker)) !== null) {
      const raw = m[1].trim();
      const fullText = raw.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '$1').trim();
      const linkMatch = raw.match(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/);
      if (linkMatch) {
        const t = linkMatch[1].replace(/\*\*([^*]+)\*\*/g, '$1').trim();
        const buttonText = GENERIC.has(t.toLowerCase()) ? fullText : t;
        if (buttonText.length > 3) exploreLinks.push({ text: buttonText, url: linkMatch[2] });
      } else {
        if (fullText.length > 5 && fullText.length < 200) askQuestions.push(fullText);
      }
    }

    return {
      mainContent,
      exploreLinks: exploreLinks.slice(0, 4),
      askQuestions: askQuestions.slice(0, 3),
    };
  };

  const processInlineFormatting = (text) => {
    /*
    Description: Processes inline formatting such as bold text, italic text, standard links, and case study links.
    Input: text (string) - A single line of text to be formatted.
    Output: Returns an array of React elements (JSX) representing the styled text segments.
    */
    if (!text) return null;
    const parts = [];
    let remaining = text;
    let index = 0;

    const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/;
    const boldRegex = /\*\*([^*]+)\*\*/;
    const bareUrlRegex = /(?<!\]\()https?:\/\/[^\s<)\]"']+/;

    while (remaining.length > 0) {
      const linkMatch = remaining.match(linkRegex);
      const boldMatch = remaining.match(boldRegex);
      const bareUrlMatch = remaining.match(bareUrlRegex);

      const candidates = [
        linkMatch && { match: linkMatch, type: 'link' },
        boldMatch && { match: boldMatch, type: 'bold' },
        bareUrlMatch && { match: bareUrlMatch, type: 'bareUrl' },
      ].filter(Boolean);

      if (candidates.length === 0) {
        parts.push(<span key={index++}>{remaining}</span>);
        break;
      }

      const earliest = candidates.reduce((a, b) =>
        b.match.index < a.match.index ? b : a
      );
      const { match: m, type } = earliest;

      if (m.index > 0) {
        parts.push(<span key={index++}>{remaining.substring(0, m.index)}</span>);
      }

      if (type === 'link') {
        parts.push(
          <a key={index++} href={m[2]} target="_blank" rel="noopener noreferrer"
            className="text-[#668f45] hover:text-[#4a6632] underline font-medium transition-colors">
            {m[1]}
          </a>
        );
      } else if (type === 'bareUrl') {
        parts.push(
          <a key={index++} href={m[0]} target="_blank" rel="noopener noreferrer"
            className="text-[#668f45] hover:text-[#4a6632] underline font-medium transition-colors break-all">
            {m[0]}
          </a>
        );
      } else if (type === 'bold') {
        const boldContent = m[1];
        const innerLinkMatch = boldContent.match(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/);
        if (innerLinkMatch) {
          parts.push(
            <strong key={index++} className="font-bold">
              <a href={innerLinkMatch[2]} target="_blank" rel="noopener noreferrer"
                className="text-[#668f45] hover:text-[#4a6632] underline font-medium transition-colors">
                {innerLinkMatch[1]}
              </a>
            </strong>
          );
        } else {
          parts.push(<strong key={index++} className="font-bold text-[#668f45]">{boldContent}</strong>);
        }
      }

      remaining = remaining.substring(m.index + m[0].length);
    }

    return parts.length > 0 ? parts : [<span key={0}>{text}</span>];
  };

  const renderText = (content) => {
    /*
    Description: The main text rendering function that splits the content by newlines and processes lists and paragraphs using inline formatting.
    Input: content (string) - The full text content of the main message.
    Output: Returns an array of React elements (JSX) representing the fully rendered message structure.
    */
    if (!content) return null;
    const cleanedContent = cleanXMLTags(content);

    const fixedContent = cleanedContent
      .replace(/\*\*(\[[^\]]+\]\(https?:\/\/[^)]+\))\*\*/g, '$1')
      .replace(/\[([^\]]+)\]\s*\n\s*\(([^)]+)\)/g, '[$1]($2)')
      .replace(/\(([^)]*)\n([^)]*)\)/g, '($1$2)');

    const lines = fixedContent.split('\n');
    const result = [];
    let listBuffer = [];
    let isOrderedList = false;
    let keyCounter = 0;

    const flushList = () => {
      /*
      Description: A helper function within renderText that flushes and renders any accumulated bullet points into a <ul> HTML list.
      Input: None (uses variables from the parent scope).
      Output: Returns a JSX <ul> element or null if the list is empty.
      */
      if (listBuffer.length === 0) return;
      if (isOrderedList) {
        result.push(<ol key={`ol-${keyCounter++}`} className="list-decimal pl-5 mb-2 space-y-1">{listBuffer}</ol>);
      } else {
        result.push(<ul key={`ul-${keyCounter++}`} className="list-disc pl-5 mb-2 space-y-1">{listBuffer}</ul>);
      }
      listBuffer = [];
      isOrderedList = false;
    };

    lines.forEach((line, lineIdx) => {
      if (!line.trim()) {
        flushList();
        result.push(<br key={`br-${lineIdx}`} />);
        return;
      }
      if (line.startsWith('###')) {
        flushList();
        result.push(<h3 key={`h-${lineIdx}`} className="text-lg font-bold mt-4 mb-2">{processInlineFormatting(line.replace(/^###\s*/, ''))}</h3>);
        return;
      }
      if (line.startsWith('##')) {
        flushList();
        result.push(<h2 key={`h2-${lineIdx}`} className="text-xl font-bold mt-4 mb-2">{processInlineFormatting(line.replace(/^##\s*/, ''))}</h2>);
        return;
      }
      if (/^[\s]*[•\-\*]\s/.test(line)) {
        const content = line.replace(/^[\s]*[•\-\*]\s*/, '');
        isOrderedList = false;
        listBuffer.push(<li key={`li-${lineIdx}`} className="mb-0.5">{processInlineFormatting(content)}</li>);
        return;
      }
      if (/^[\s]*\d+\.\s/.test(line)) {
        const content = line.replace(/^[\s]*\d+\.\s*/, '');
        isOrderedList = true;
        listBuffer.push(<li key={`li-${lineIdx}`} className="mb-0.5">{processInlineFormatting(content)}</li>);
        return;
      }
      flushList();
      result.push(<p key={`p-${lineIdx}`} className="mb-2">{processInlineFormatting(line)}</p>);
    });

    flushList();
    return result;
  };

  const { mainContent, exploreLinks, askQuestions } = parseFollowUps(text || '');

  return (
    <div className="text-sm leading-relaxed">
      {renderText(mainContent)}

      {exploreLinks.length > 0 && (
        <div className="mt-3">
          <p className="text-sm font-bold text-gray-800 mb-2">✨ You might also want to know:</p>
          <div className="flex flex-col items-start gap-2">
            {exploreLinks.map((item, i) => (
              <a
                key={i}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs bg-[#668f45]/8 border border-[#668f45]/40 text-[#668f45] px-3 py-1.5 rounded-xl font-medium hover:bg-[#668f45] hover:text-white transition-all hover:-translate-y-0.5 cursor-pointer"
              >
                {item.text}
              </a>
            ))}
          </div>
        </div>
      )}

      {isBot && !streaming && askQuestions.length > 0 && (
        <div className="mt-2">
          <p className="text-xs font-bold text-gray-700 mb-1.5">💬 You might also want to know:</p>
          <div className="flex flex-col items-start gap-2">
            {askQuestions.map((q, i) => (
              <button
                key={i}
                onClick={() => onFollowUp && onFollowUp(q)}
                className="text-xs bg-[#28b9d2]/8 border border-[#28b9d2]/40 text-[#28b9d2] px-3 py-1.5 rounded-xl font-medium hover:bg-[#28b9d2] hover:text-white transition-all hover:-translate-y-0.5 cursor-pointer text-left"
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

// Copy + thumbs up/down feedback row, shown under a finished bot message.
const MessageActions = ({ text, feedback, onFeedback }) => {
  /*
  Description: A small toolbar rendered under a completed bot message — lets the person
  copy the raw answer text to their clipboard, and leave a quick thumbs-up/down reaction.
  Feedback is purely local UI state (no backend call); it just gives the person a way to
  react and gives visual acknowledgement.
  Input: { text (string), feedback ('up' | 'down' | null), onFeedback (function) }.
  Output: Returns a rendered JSX row of action buttons.
  */
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(text || '').then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  return (
    <div className="flex items-center gap-1 mt-1.5">
      <button
        onClick={handleCopy}
        title={copied ? 'Copied!' : 'Copy answer'}
        className="p-1.5 rounded-lg text-gray-400 hover:text-[#668f45] hover:bg-[#668f45]/10 transition-all"
      >
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
      <button
        onClick={() => onFeedback('up')}
        title="Helpful"
        className={`p-1.5 rounded-lg transition-all ${feedback === 'up' ? 'text-[#668f45] bg-[#668f45]/15' : 'text-gray-400 hover:text-[#668f45] hover:bg-[#668f45]/10'}`}
      >
        <ThumbsUp className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={() => onFeedback('down')}
        title="Not helpful"
        className={`p-1.5 rounded-lg transition-all ${feedback === 'down' ? 'text-red-500 bg-red-50' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`}
      >
        <ThumbsDown className="w-3.5 h-3.5" />
      </button>
      {feedback === 'up' && <span className="text-[10px] text-gray-400 font-medium ml-1">Glad this was helpful!</span>}
      {feedback === 'down' && <span className="text-[10px] text-gray-400 font-medium ml-1">Thanks — we'll work on doing better.</span>}
    </div>
  );
};

// Quick Step Sidebar Component
// Sidebar quick-question buttons — always visible below the Quick Step links.
// Clicking one sends that question straight into the chat.
const SIDEBAR_QUESTIONS = [
  "What computer vision problems have you solved for enterprises?",
  "Can you help us pick the right AI use case to start with?",
  "Can you work inside our existing Microsoft stack?",
  "Can we start from something you've already built rather than from scratch?",
];

const QuickStepSidebar = ({ onQuestionClick }) => {
  /*
  Description: A React component that displays a sidebar with a "Quick Step" section.
  The section is always expanded — it shows all 7 links at once (no individual expand/collapse) —
  each row is a direct link that opens its target page in a new tab. Only the whole sidebar itself
  can be collapsed down to icon-only width via the menu button.
  Input: None.
  Output: Returns a rendered JSX element containing the sidebar.
  */
  const [isCollapsed, setIsCollapsed] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 640 : false));

  return (
    <div className={`flex ${isCollapsed ? 'w-14' : 'w-72'} h-full bg-gradient-to-br from-[#f8faf9] to-[#f0f4f1] border-r-2 border-[#668f45]/20 flex-col transition-all duration-300 ease-in-out`}>
      {!isCollapsed ? (
        <>
          <div className="p-4 border-b-2 border-[#668f45]/20">
            <div className="flex items-center gap-2">
              <div
                className="flex-1 flex items-center justify-between gap-2 bg-[#668f45] text-white rounded-xl px-4 py-2.5 font-extrabold text-sm"
              >
                <span className="flex items-center gap-2">
                  Quick Steps
                </span>
              </div>
              <button
                onClick={() => setIsCollapsed(true)}
                className="bg-[#668f45]/10 hover:bg-[#668f45] hover:text-white text-[#668f45] rounded-lg p-2.5 transition-all duration-200 flex-shrink-0"
                title="Collapse sidebar"
              >
                <PanelLeftClose className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {QUICK_STEP_LINKS.map((link, idx) => {
              const Icon = link.icon;
              return (
                <a
                  key={idx}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between gap-2 bg-white rounded-xl shadow-sm border-2 p-3 hover:shadow-md hover:bg-[#0ea5e9]/10 hover:-translate-y-0.5 transition-all group"
                  style={{ borderColor: link.color }}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <Icon className="w-4 h-4 flex-shrink-0" style={{ color: link.color }} />
                    <span className="font-bold text-gray-800 text-xs leading-snug">{link.label}</span>
                  </span>
                  <ExternalLink className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 flex-shrink-0 transition-colors" />
                </a>
              );
            })}

            <div className="pt-3 mt-1 border-t-2 border-[#668f45]/20 space-y-2">
              <p className="text-[11px] font-extrabold text-[#668f45] uppercase tracking-wide px-1 mb-1">Frequently Asked Questions</p>
              {SIDEBAR_QUESTIONS.map((q, idx) => (
                <button
                  key={idx}
                  onClick={() => onQuestionClick && onQuestionClick(q)}
                  className="w-full text-left flex items-center gap-2 bg-white rounded-xl shadow-sm border-2 p-3 hover:shadow-md hover:bg-[#0ea5e9]/10 hover:-translate-y-0.5 transition-all"
                  style={{ borderColor: '#0ea5e9' }}
                >
                  <span className="font-medium text-gray-700 text-xs leading-snug">{q}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="p-4 border-t-2 border-[#668f45]/20 bg-white mt-auto">
            <div className="text-center">
              <p className="text-[10px] text-gray-500 font-medium mb-1">Developed and Maintained</p>
              <p className="text-xs font-bold text-[#668f45]">by ThirdEye Data</p>
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center gap-3 py-4">
          <button
            onClick={() => setIsCollapsed(false)}
            className="p-2 rounded-lg bg-[#668f45] text-white hover:bg-[#4a6632] transition-all duration-200 mb-1"
            title="Expand sidebar"
          >
            <PanelLeftOpen className="w-4 h-4" />
          </button>
          <div className="space-y-3">
            {QUICK_STEP_LINKS.map((link, idx) => {
              const Icon = link.icon;
              return (
                <a
                  key={idx}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 rounded-lg bg-white shadow-sm hover:shadow-md transition-all cursor-pointer border-2 hover:scale-110 block"
                  style={{ borderColor: link.color }}
                  title={link.label}
                >
                  <Icon className="w-5 h-5" style={{ color: link.color }} />
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default function TEDRAChatUI({ onOpenAdmin }) {
  /*
  Description: The main React application component for the TEDRA Chat interface, managing chat state, sessions, and UI interactions.
  Input: None.
  Output: Returns the complete application JSX layout.
  */
  const [chatOpen, setChatOpen] = useState(true);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [isFloatingButton, setIsFloatingButton] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [hideExpandControls, setHideExpandControls] = useState(false);

  // Anonymous session tracking
  const [sessionId, setSessionId] = useState(() => {
    try {
      const saved = localStorage.getItem('tedra_session_id');
      if (saved) return saved;
      const newId = 'tedra_' + (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : (Date.now().toString(36) + Math.random().toString(36).substring(2, 8)));
      localStorage.setItem('tedra_session_id', newId);
      return newId;
    } catch (_) {
      return 'tedra_session_' + Date.now();
    }
  });

  // True whenever this page is running inside an iframe (e.g. the WordPress popup)
  // rather than being visited directly - used to strip the extra background/padding
  // and the internal "closed" reopen bubble, since the embedding page already
  // provides its own launcher and its own backdrop.
  const isEmbedded = typeof window !== 'undefined' && window.parent && window.parent !== window;

  const [messages, setMessages] = useState([
    { 
      type: 'bot', 
      text: "Hi! I'm TEDRA, your AI assistant from ThirdEye Data. I can help you explore our AI solutions, demo applications, and connect you with our experts. What would you like to know?",
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [firstMessageSent, setFirstMessageSent] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const recognitionRef = useRef(null);

  const scrollToBottom = (force = false) => {
    /*
    Description: Smoothly scrolls the chat window to the most recent message.
    Input: force (boolean, optional) - Whether to force the scroll ignoring the user's current scroll position.
    Output: None (Updates the DOM scroll position).
    */
    if (!force) {
      // Only auto-scroll if the user is already near the bottom (within 150px)
      // This lets the user freely scroll up while a response is streaming
      const container = chatContainerRef.current;
      if (container) {
        const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        if (distanceFromBottom > 150) return; // user has scrolled up — don't interrupt
      }
    }
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom(); // smart scroll — respects user scroll position during streaming
  }, [messages]);

  useEffect(() => {
    /*
    Description: Whenever isMaximized changes and this page is embedded (e.g. the WordPress
    floating popup), tells the parent page via postMessage so it can grow its own small
    fixed-size overlay container to fill the screen. This only gives the app room to work
    with - the app's own layout always stays the same rounded floating card (see the render
    below), never a borderless full-bleed mode, so it reads as "expanded" rather than "full screen".
    Input: None.
    Output: None (posts a message to the parent when embedded).
    */
    if (isEmbedded) {
      window.parent.postMessage({ type: 'tedra-maximize', value: isMaximized }, '*');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMaximized]);

  const sendMessage = async (question = input) => {
    /*
    Description: Handles sending a user message to the backend API, managing loading states, and appending the AI's response to the chat history.
    Input: question (string) - The user's input text (defaults to the current input state).
    Output: None (Asynchronous function that updates messages state).
    */
    if (!question.trim() || loading) return;

    lastActivityRef.current = Date.now();
    if (!firstMessageSent) setFirstMessageSent(true);

    const userMessage = { type: 'user', text: question, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    // Force-scroll to bottom when user sends — they expect to see their own message
    setTimeout(() => scrollToBottom(true), 50);

    // Add an empty bot message placeholder that we'll fill as chunks stream in
    const botPlaceholder = { type: 'bot', text: '', timestamp: new Date(), streaming: true };
    setMessages(prev => [...prev, botPlaceholder]);
    setTimeout(() => scrollToBottom(true), 80);

        // Hardcoded answers bypass the backend, but stream with randomized timing
    // so the pacing doesn't look mechanically uniform (a dead giveaway otherwise).
    const cannedAnswer = CANNED_ANSWERS[question.trim().toLowerCase()];
    if (cannedAnswer) {
      // Mimic the network round-trip + "thinking" delay before any text appears —
      // the empty-placeholder dots (already in your UI) will show during this gap.
      await new Promise(r => setTimeout(r, 500 + Math.random() * 700));

      const words = cannedAnswer.split(' ');
      let streamed = '';
      let i = 0;
      while (i < words.length) {
        // Send 1–3 words per tick, like real token chunks rather than exactly one word each time
        const chunkSize = 1 + Math.floor(Math.random() * 3);
        const chunk = words.slice(i, i + chunkSize).join(' ');
        streamed += (streamed ? ' ' : '') + chunk;
        i += chunkSize;
        const isLast = i >= words.length;

        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            type: 'bot',
            text: streamed,
            timestamp: new Date(),
            streaming: !isLast,
          };
          return updated;
        });
        // Randomized delay per tick instead of a fixed one
        await new Promise(r => setTimeout(r, 25 + Math.random() * 40));
      }
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/tedra/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, session_id: sessionId }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';
      let buffer = '';
      let streamDone = false;

      while (true) {
        const { value, done } = await reader.read();
        if (done || streamDone) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // last incomplete line stays in buffer

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          if (!raw) continue;

          try {
            const payload = JSON.parse(raw);

            if (payload.error) {
              accumulatedText = "Sorry, something went wrong. Please try again or [contact us](https://thirdeyedata.ai/contact-us).";
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { type: 'bot', text: accumulatedText, timestamp: new Date() };
                return updated;
              });
              streamDone = true;
              break;
            }

            if (payload.chunk) {
              accumulatedText += payload.chunk;
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                  type: 'bot',
                  text: accumulatedText,
                  timestamp: new Date(),
                  streaming: true,
                };
                return updated;
              });
              if (!payload.done) await new Promise(r => setTimeout(r, 35));
            }

            if (payload.done) {
              // Immediately flip streaming off — triggers fade-in right away
              setMessages(prev => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last) updated[updated.length - 1] = { ...last, text: accumulatedText, streaming: false };
                return updated;
              });
              streamDone = true;
              break;
            }
          } catch (_) {
            // Ignore malformed SSE lines
          }
        }
      }

      // Finalise — remove streaming flag
      setMessages(prev => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last && last.streaming) {
          updated[updated.length - 1] = { ...last, streaming: false };
        }
        return updated;
      });

    } catch (error) {
      // Fallback to non-streaming endpoint on network error
      try {
        const fallback = await fetch('/api/tedra', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ question, session_id: sessionId }),
        });
        const data = await fallback.json();
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { type: 'bot', text: data.answer, timestamp: new Date() };
          return updated;
        });
      } catch (_) {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            type: 'bot',
            text: "Sorry, I'm having trouble connecting. Please try again shortly.",
            timestamp: new Date(),
          };
          return updated;
        });
      }
    } finally {
      setLoading(false);
      // Always clear streaming flag — catches error paths where done event never fires
      setMessages(prev => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last && last.streaming) {
          updated[updated.length - 1] = { ...last, streaming: false };
        }
        return updated;
      });
    }
  };

  useEffect(() => {
    /*
    Description: On initial load, checks the URL for a `q` query parameter (used when this
    widget is embedded and launched from an external site, e.g. the search bar on the main
    ThirdEye Data WordPress site) and, if present, expands the chat and auto-sends that
    question instead of waiting for the user to type it again. Also checks for a `mode=embed_large`
    flag, set only by the WordPress search-bar popup (not the floating-bubble popup), which hides
    the minimize/expand-to-corner controls that don't make sense inside that larger modal.
    Input: None.
    Output: None (may call setIsMinimized, setHideExpandControls and sendMessage; rewrites the URL to drop `q`).
    */
    const params = new URLSearchParams(window.location.search);
    const initialQuestion = params.get('q');
    const mode = params.get('mode');

    if (mode === 'embed_large') {
      setHideExpandControls(true);
    }

    if (initialQuestion && initialQuestion.trim()) {
      setIsMinimized(false);
      sendMessage(initialQuestion);

      // Remove `q` from the URL so refreshing the page doesn't resend the same question
      params.delete('q');
      const newSearch = params.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
      window.history.replaceState({}, '', newUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    /*
    Description: Runs a periodic check (every minute) and auto-clears the conversation if no
    message has been sent or received for over 30 minutes - so a popup left open (but hidden,
    e.g. via the WordPress bubble's minimize) doesn't keep an old, possibly stale conversation
    around indefinitely. A manual clear, or a full page refresh, both already reset the
    conversation on their own and are unaffected by this timer.
    Input: None.
    Output: None (may call clearChat on the interval).
    */
    const INACTIVITY_LIMIT_MS = 30 * 60 * 1000;
    const CHECK_INTERVAL_MS = 60 * 1000;

    const interval = setInterval(() => {
      if (Date.now() - lastActivityRef.current > INACTIVITY_LIMIT_MS) {
        clearChat();
        lastActivityRef.current = Date.now();
      }
    }, CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyPress = (e) => {
    /*
    Description: Listens for the Enter key press in the input field to trigger sending the message.
    Input: e (Event) - The keyboard event object.
    Output: None (Calls sendMessage on Enter key).
    */
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const toggleVoiceInput = () => {
    /*
    Description: Starts or stops browser-native speech-to-text (Web Speech API) and streams
    the transcript into the input field as the person talks. Silently does nothing if the
    browser doesn't support it (currently Chrome/Edge only) since there's no good fallback.
    Input: None.
    Output: None (updates `input` and `isListening`; manages `recognitionRef`).
    */
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.continuous = false;
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setInput(transcript);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  };

  const closeChat = () => {
    /*
    Description: Closes the chat. When embedded inside an iframe (e.g. the WordPress popup),
    this only notifies the parent page via postMessage so IT hides its own wrapper - it does
    NOT set chatOpen to false here, because the host has no way to tell this iframe to flip it
    back to true again when the visitor reopens via the host's own floating launcher button
    (that button just re-shows the host's wrapper via CSS; it never messages back in). Leaving
    chatOpen untouched means the app is already in the right state and renders immediately the
    next time the host makes the wrapper visible, instead of showing a blank iframe. When NOT
    embedded (a direct visit to tedra.thirdeyedata.ai), there is no host wrapper, so this app
    must hide itself and show its own reopen bubble - hence setChatOpen(false) only applies there.
    Input: None.
    Output: None (calls setChatOpen when standalone, or posts a message to the parent when embedded).
    */
    if (isEmbedded) {
      window.parent.postMessage({ type: 'tedra-close' }, '*');
    } else {
      setChatOpen(false);
    }
  };

  const handleMinimizeClick = () => {
    /*
    Description: Handles the header's "minimize" (Minus icon) click. When embedded (e.g. the
    WordPress popup), this does NOT show TEDRA's own internal floating reopen pill - instead it
    tells the parent page to hide its own wrapper via postMessage, leaving every internal state
    (conversation, isMinimized/isMaximized) untouched so it resumes exactly as it was when the
    person reopens it via the parent page's own launcher. When not embedded (a direct visit to
    tedra.thirdeyedata.ai), this keeps the original standalone behavior unchanged.
    Input: None.
    Output: None (posts a message to the parent when embedded, or updates local state otherwise).
    */
    if (isEmbedded) {
      window.parent.postMessage({ type: 'tedra-minimize' }, '*');
    } else {
      setIsFloatingButton(true);
      setIsMinimized(false);
      setIsMaximized(false);
    }
  };

  const clearChat = () => {
    /*
    Description: Clears the current chat history, resets session state, and restarts the conversation with the initial greeting.
    Input: None.
    Output: None (Updates React states and resets the backend session).
    */
    if (sessionId) {
      fetch('/api/clear-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId }),
      }).catch(() => {});
    }

    try {
      const newId = 'tedra_' + (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : (Date.now().toString(36) + Math.random().toString(36).substring(2, 8)));
      localStorage.setItem('tedra_session_id', newId);
      setSessionId(newId);
    } catch (_) {}

    setMessages([{
      type: 'bot',
      text: "Hi! I'm TEDRA, your AI assistant from ThirdEye Data. I can help you explore our AI solutions, demo applications, and connect you with our experts. What would you like to know?",
      timestamp: new Date()
    }]);
    setFirstMessageSent(false);
    setShowMenu(false);
  };

  const downloadChat = () => {
    /*
    Description: Downloads the entire chat history as a formatted text file (.txt).
    Input: None.
    Output: None (Triggers a browser download of the chat transcript).
    */
    const chatText = messages.map(msg => 
      `[${msg.timestamp.toLocaleString()}] ${msg.type === 'user' ? 'You' : 'TEDRA'}: ${msg.text}`
    ).join('\n\n');
    const blob = new Blob([chatText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `TEDRA-chat-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    setShowMenu(false);
  };

  const handleFeedback = (index, type) => {
    /*
    Description: Records a thumbs-up/down reaction against a specific message, toggling it
    off if the same reaction is clicked again. Local UI state only — not sent anywhere.
    Input: index (number) - the message's index in the `messages` array. type ('up' | 'down').
    Output: None (Updates the `messages` state).
    */
    setMessages(prev => prev.map((m, i) => (
      i === index ? { ...m, feedback: m.feedback === type ? null : type } : m
    )));
  };

  return (
    <div className={`fixed inset-0 flex items-center justify-center overscroll-contain ${isEmbedded ? '' : 'p-4 bg-gradient-to-br from-[#f0f4f1] to-[#e8f0ea]'}`}>
      {!chatOpen && !isEmbedded && (
        <button
          onClick={() => setChatOpen(true)}
          className="group fixed bottom-6 right-6 bg-gradient-to-r from-[#7db34f] to-[#668f45] text-white px-8 py-5 rounded-2xl shadow-2xl hover:shadow-[0_20px_60px_rgba(102,143,69,0.4)] transition-all duration-300 hover:-translate-y-1 flex items-center gap-3 z-50"
        >
          <img src="https://ted2022stage.wpengine.com/wp-content/uploads/2017/06/ai-icon-e1542909412513-1-150x150.png" alt="TEDRA" className="w-10 h-10 rounded-full border-2 border-white/30" />
          <div className="text-left">
            <div className="font-extrabold text-lg">TEDRA</div>
            <div className="text-xs text-white/90 font-medium">AI Assistant</div>
          </div>
        </button>
      )}

      {chatOpen && isFloatingButton && !isEmbedded && (
        <button
          onClick={() => { setIsFloatingButton(false); setIsMaximized(false); setIsMinimized(false); }}
          className="fixed bottom-6 right-6 bg-gradient-to-r from-[#7db34f] to-[#668f45] text-white px-6 py-4 rounded-2xl shadow-2xl hover:shadow-3xl transition-all hover:scale-105 flex items-center gap-3 z-50 font-bold text-lg"
        >
          <img src="https://ted2022stage.wpengine.com/wp-content/uploads/2017/06/ai-icon-e1542909412513-1-150x150.png" alt="TEDRA" className="w-10 h-10 rounded-full border-2 border-white/30" />
          <span>Ask TEDRA</span>
        </button>
      )}

      {chatOpen && !isFloatingButton && (
        <div
          className={`bg-white shadow-2xl overflow-hidden ${isMinimized ? (isEmbedded ? 'w-full h-full rounded-l-3xl' : 'w-[420px] max-w-full h-full rounded-l-3xl') : (hideExpandControls ? 'w-full h-full rounded-none flex' : 'w-[95vw] h-[90vh] max-w-[1400px] rounded-3xl flex')} ${!isMinimized ? 'animate-slideIn' : ''} transition-all duration-300`}
          style={{
            position: 'fixed',
            ...(isMinimized ? { top: 0, right: 0, bottom: 0 } :
                hideExpandControls ? { top: 0, left: 0, right: 0, bottom: 0 } :
                { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' })
          }}
        >
          {!isMinimized && (
            <>
              <QuickStepSidebar onQuestionClick={(q) => sendMessage(q)} />
              <div className="flex-1 flex flex-col min-w-0">
                {/* Header */}
                <div className="bg-gradient-to-r from-[#7db34f] to-[#668f45] text-white px-4 py-2.5 flex items-center justify-between shadow-lg flex-shrink-0">
                  <div className="flex items-center gap-2.5">
                    <img src="https://ted2022stage.wpengine.com/wp-content/uploads/2017/06/ai-icon-e1542909412513-1-150x150.png" alt="TEDRA" className="w-8 h-8 rounded-full border-2 border-white/40 shadow-lg" />
                    <div>
                      <h2 className="font-extrabold text-base tracking-tight">TEDRA</h2>
                      <p className="text-[10px] text-white/90 font-medium">ThirdEye Data AI Assistant</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <button onClick={() => setShowMenu(!showMenu)} className="w-9 h-9 hover:bg-white/20 rounded-xl flex items-center justify-center transition-all">
                        <Menu className="w-5 h-5" />
                      </button>
                      {showMenu && (
                        <div className="absolute right-0 top-12 bg-white rounded-xl shadow-2xl p-2 w-48 z-50 border border-gray-200">
                          <button onClick={clearChat} className="w-full text-left px-4 py-2.5 hover:bg-gray-50 rounded-lg text-sm font-semibold text-gray-700 transition-colors">Clear Chat</button>
                          <button onClick={downloadChat} className="w-full text-left px-4 py-2.5 hover:bg-gray-50 rounded-lg text-sm font-semibold text-gray-700 flex items-center gap-2 transition-colors">
                            <Download className="w-4 h-4" />Download Chat
                          </button>
                          <button onClick={() => { setShowInfo(!showInfo); setShowMenu(false); }} className="w-full text-left px-4 py-2.5 hover:bg-gray-50 rounded-lg text-sm font-semibold text-gray-700 flex items-center gap-2 transition-colors">
                            <Info className="w-4 h-4" />About TEDRA
                          </button>
                          {onOpenAdmin && (
                            <button onClick={() => { setShowMenu(false); onOpenAdmin(); }} className="w-full text-left px-4 py-2.5 hover:bg-gray-50 rounded-lg text-sm font-semibold text-[#668f45] flex items-center gap-2 transition-colors border-t border-gray-100">
                              <ShieldCheck className="w-4 h-4" />Admin Dashboard
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    {!hideExpandControls && (
                      <>
                        <button onClick={handleMinimizeClick} className="w-9 h-9 hover:bg-white/20 rounded-xl flex items-center justify-center transition-all" title="Minimize to floating button">
                          <Minus className="w-5 h-5" />
                        </button>
                        <button onClick={() => { setIsMinimized(true); setIsFloatingButton(false); setIsMaximized(false); }} className="w-9 h-9 hover:bg-white/20 rounded-xl flex items-center justify-center transition-all" title="Minimize to corner">
                          <Maximize2 className="w-5 h-5" />
                        </button>
                      </>
                    )}
                    <button onClick={closeChat} className="w-9 h-9 hover:bg-white/20 rounded-xl flex items-center justify-center transition-all">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {showInfo && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowInfo(false)} />
                    <div className="relative z-50 bg-blue-50 border-b-2 border-blue-200 px-6 py-4">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h3 className="font-bold text-blue-900 mb-1">About TEDRA</h3>
                          <p className="text-sm text-blue-800 leading-relaxed">
                            TEDRA is ThirdEye Data's AI assistant designed to help visitors quickly explore our AI services, solutions, use cases, and case studies. It uses retrieval-augmented generation (RAG) to provide context-aware responses based on approved company knowledge.
                          </p>
                        </div>
                        <button onClick={() => setShowInfo(false)} className="text-blue-600 hover:text-blue-800 transition-colors flex-shrink-0">
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {/* Messages */}
                <div ref={chatContainerRef} className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain p-6 bg-gradient-to-br from-[#fafbfc] to-[#f5f7fa]">
                  <div>
                    <div className="text-center py-4 mb-4">
                      <p className="text-gray-700 font-medium text-sm max-w-2xl mx-auto">
                        <span className="font-extrabold text-gray-900">TEDRA</span> – ThirdEye Data Response Assistant is an AI assistant that can answer anything about ThirdEye Data, from services and solutions to case studies and industry expertise.
                      </p>
                    </div>

                    <div className="flex gap-3 mb-6">
                      <img src="https://ted2022stage.wpengine.com/wp-content/uploads/2017/06/ai-icon-e1542909412513-1-150x150.png" alt="TEDRA" className="w-10 h-10 rounded-full flex-shrink-0" />
                      <div className="flex-1 max-w-[85%]">
                        <div className="text-sm leading-relaxed text-gray-900">{messages[0].text}</div>
                        <MessageActions text={messages[0].text} feedback={messages[0].feedback} onFeedback={(type) => handleFeedback(0, type)} />
                        <div className="text-xs text-gray-400 mt-2 font-medium">Just now</div>
                      </div>
                    </div>

                    {messages.slice(1).map((msg, idx) => (
                      <div key={idx} className={`flex gap-3 mb-6 ${msg.type === 'user' ? 'justify-end' : ''}`}>
                        {msg.type === 'bot' && (
                          <img src="https://ted2022stage.wpengine.com/wp-content/uploads/2017/06/ai-icon-e1542909412513-1-150x150.png" alt="TEDRA" className="w-10 h-10 rounded-full flex-shrink-0" />
                        )}
                        <div className={`${msg.type === 'user' ? 'max-w-[78%]' : 'flex-1 max-w-[85%]'}`}>
                          <div className={`${msg.type === 'user' ? 'bg-white/70 backdrop-blur-sm border-2 border-[#668f45]/60 shadow-lg p-4 rounded-2xl rounded-tr-md text-gray-900 font-semibold relative overflow-hidden' : 'text-sm leading-relaxed text-gray-900'}`}>
                            {msg.type === 'user' && <div className="absolute inset-0 bg-gradient-to-br from-[#7db34f]/8 to-[#668f45]/5 pointer-events-none"></div>}
                            <div className="relative z-10">
                              {/* Show empty streaming placeholder as dots until first chunk arrives */}
                              {msg.streaming && !msg.text ? (
                                <div className="flex gap-1.5 py-1">
                                  <div className="w-2 h-2 bg-[#668f45] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                  <div className="w-2 h-2 bg-[#668f45] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                  <div className="w-2 h-2 bg-[#668f45] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                </div>
                              ) : (
                                <>
                                  <MessageContent text={msg.text} isBot={msg.type === 'bot'} streaming={!!msg.streaming} onFollowUp={(q) => { setIsMinimized(false); setIsMaximized(isEmbedded); sendMessage(q); }} />
                                  {false && <span className="inline-block w-[2px] h-[1em] bg-[#668f45] ml-0.5 align-middle" style={{ animation: 'tedra-blink 0.8s step-end infinite' }}></span>}
                                  {msg.type === 'bot' && !msg.streaming && (
                                    <MessageActions text={msg.text} feedback={msg.feedback} onFeedback={(type) => handleFeedback(idx + 1, type)} />
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                          <div className="text-xs text-gray-400 mt-2 font-medium">
                            {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Loading dots — only when no streaming placeholder exists */}
                    {loading && !messages.some(m => m.streaming) && (
                      <div className="flex gap-3 mb-6">
                        <img src="https://ted2022stage.wpengine.com/wp-content/uploads/2017/06/ai-icon-e1542909412513-1-150x150.png" alt="TEDRA" className="w-10 h-10 rounded-full flex-shrink-0" />
                        <div className="bg-[#668f45]/8 rounded-xl p-4 w-fit">
                          <div className="flex gap-1.5">
                            <div className="w-2 h-2 bg-[#668f45] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                            <div className="w-2 h-2 bg-[#668f45] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                            <div className="w-2 h-2 bg-[#668f45] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                          </div>
                        </div>
                      </div>
                    )}

                    {!firstMessageSent && (
                      <div className="flex flex-wrap gap-2 justify-center mt-5">
                        {SAMPLE_QUESTIONS.map((q, i) => (
                          <button key={i} onClick={() => sendMessage(q)} disabled={loading} className="text-xs bg-[#28b9d2]/8 border-2 border-[#28b9d2] text-[#28b9d2] px-4 py-2 rounded-xl font-bold hover:bg-[#28b9d2]/18 transition-all hover:-translate-y-0.5 disabled:opacity-50">
                            {q}
                          </button>
                        ))}
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </div>

                {/* Footer */}
                <div className="bg-white px-4 py-3 border-t border-gray-200/60 flex-shrink-0">
                  <div className="flex gap-2 bg-gray-50 border-2 border-gray-200 rounded-2xl p-1.5 mb-2 focus-within:border-[#668f45] focus-within:bg-white focus-within:shadow-lg transition-all">
                    <button
                      onClick={toggleVoiceInput}
                      title={isListening ? 'Stop listening' : 'Ask by voice'}
                      className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-all ${isListening ? 'bg-red-500 text-white animate-pulse' : 'text-gray-400 hover:text-[#668f45] hover:bg-[#668f45]/10'}`}
                    >
                      {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                    </button>
                    <input
                      type="text"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Ask me any questions about ThirdEye Data... (Enter to send)"
                      disabled={loading}
                      maxLength={500}
                      className="flex-1 bg-transparent px-3 py-2 outline-none text-gray-900 font-medium placeholder-gray-400 text-sm min-w-0"
                    />
                    <div className="flex gap-1.5 flex-shrink-0">
                      <button onClick={() => sendMessage()} disabled={loading || !input.trim()} className="bg-gradient-to-r from-[#7db34f] to-[#668f45] text-white px-5 py-2 rounded-xl font-extrabold hover:from-[#6ba043] hover:to-[#4a6632] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg text-sm">
                        Ask
                      </button>
                      <button onClick={() => sendMessage('Connect me with the Sales Team')} title="Talk to a ThirdEye Data expert" className="bg-[#28b9d2]/12 border-2 border-[#28b9d2] text-[#28b9d2] px-4 py-2 rounded-xl text-xs font-extrabold hover:bg-[#28b9d2] hover:text-white transition-all whitespace-nowrap">
                        Connect
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-[9px] font-medium ${input.length > 450 ? 'text-red-400' : 'text-gray-400'}`}>{input.length}/500</span>
                    <div className="text-center text-[9px] text-gray-400">
                      <span className="font-medium">Powered by ThirdEye Data</span>
                      <span className="mx-1.5 opacity-50">·</span>
                      <a href="https://thirdeyedata.ai/contact-us/" target="_blank" rel="noopener noreferrer" className="text-[#28b9d2] font-semibold hover:underline">Get Your Customised Version</a>
                    </div>
                    <span className="text-[9px] text-gray-300 select-none">↵ send</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Minimized Bar */}
          {isMinimized && (
            <div className="h-full flex flex-col bg-gradient-to-b from-[#7db34f] to-[#668f45]">
              <div className="bg-gradient-to-r from-[#7db34f] to-[#668f45] text-white px-4 py-2.5 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2.5">
                  <img src="https://ted2022stage.wpengine.com/wp-content/uploads/2017/06/ai-icon-e1542909412513-1-150x150.png" alt="TEDRA" className="w-8 h-8 rounded-full border-2 border-white/40 shadow-lg" />
                  <div>
                    <h2 className="font-extrabold text-base tracking-tight">TEDRA</h2>
                    <p className="text-[10px] text-white/90 font-medium">ThirdEye Data AI Assistant</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => { setIsMinimized(false); setIsMaximized(isEmbedded); }} className="w-8 h-8 hover:bg-white/20 rounded-xl flex items-center justify-center transition-all" title="Expand chat">
                    <Maximize2 className="w-4 h-4" />
                  </button>
                  <button onClick={closeChat} className="w-8 h-8 hover:bg-white/20 rounded-xl flex items-center justify-center transition-all" title="Close chat">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div ref={chatContainerRef} className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain p-4 bg-gradient-to-br from-[#fafbfc] to-[#f5f7fa]">
                {!firstMessageSent ? (
                  <>
                    <div className="text-center py-5 mb-4">
                      <p className="text-gray-700 font-medium text-sm px-2">
                        <span className="font-extrabold text-gray-900">TEDRA</span> – ThirdEye Data Response Assistant is an AI assistant that can answer anything about ThirdEye Data, from services and solutions to case studies and industry expertise.
                      </p>
                    </div>
                    <div className="flex gap-3 mb-5">
                      <img src="https://ted2022stage.wpengine.com/wp-content/uploads/2017/06/ai-icon-e1542909412513-1-150x150.png" alt="TEDRA" className="w-8 h-8 rounded-full flex-shrink-0" />
                      <div className="flex-1">
                        <div className="text-sm leading-relaxed text-gray-900">{messages[0].text}</div>
                        <MessageActions text={messages[0].text} feedback={messages[0].feedback} onFeedback={(type) => handleFeedback(0, type)} />
                        <div className="text-xs text-gray-400 mt-1 font-medium">Just now</div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      {SAMPLE_QUESTIONS.map((q, i) => (
                        <button key={i} onClick={() => { setIsMinimized(false); setIsMaximized(isEmbedded); sendMessage(q); }} disabled={loading} className="text-xs bg-[#28b9d2]/8 border-2 border-[#28b9d2] text-[#28b9d2] px-4 py-2 rounded-xl font-bold hover:bg-[#28b9d2]/18 hover:-translate-y-0.5 transition-all disabled:opacity-50 text-center w-full">
                          {q}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex gap-3 mb-4">
                      <img src="https://ted2022stage.wpengine.com/wp-content/uploads/2017/06/ai-icon-e1542909412513-1-150x150.png" alt="TEDRA" className="w-8 h-8 rounded-full flex-shrink-0" />
                      <div className="flex-1">
                        <div className="text-sm leading-relaxed text-gray-900">{messages[0].text}</div>
                        <MessageActions text={messages[0].text} feedback={messages[0].feedback} onFeedback={(type) => handleFeedback(0, type)} />
                        <div className="text-xs text-gray-400 mt-1 font-medium">Just now</div>
                      </div>
                    </div>
                    {messages.slice(1).map((msg, idx) => (
                      <div key={idx} className={`flex gap-3 mb-4 ${msg.type === 'user' ? 'justify-end' : ''}`}>
                        {msg.type === 'bot' && (
                          <img src="https://ted2022stage.wpengine.com/wp-content/uploads/2017/06/ai-icon-e1542909412513-1-150x150.png" alt="TEDRA" className="w-8 h-8 rounded-full flex-shrink-0" />
                        )}
                        <div className={`${msg.type === 'user' ? 'max-w-[80%]' : 'flex-1 max-w-[88%]'}`}>
                          <div className={`${msg.type === 'user' ? 'bg-white/70 border-2 border-[#668f45]/60 shadow p-3 rounded-2xl rounded-tr-md text-gray-900 font-semibold' : 'text-sm leading-relaxed text-gray-900'}`}>
                            <MessageContent text={msg.text} isBot={msg.type === 'bot'} streaming={!!msg.streaming} onFollowUp={(q) => { setIsMinimized(false); setIsMaximized(isEmbedded); sendMessage(q); }} />
                            {msg.type === 'bot' && !msg.streaming && (
                              <MessageActions text={msg.text} feedback={msg.feedback} onFeedback={(type) => handleFeedback(idx + 1, type)} />
                            )}
                          </div>
                          <div className="text-xs text-gray-400 mt-1 font-medium">
                            {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>
                    ))}
                    {loading && (
                      <div className="flex gap-3 mb-4">
                        <img src="https://ted2022stage.wpengine.com/wp-content/uploads/2017/06/ai-icon-e1542909412513-1-150x150.png" alt="TEDRA" className="w-8 h-8 rounded-full flex-shrink-0" />
                        <div className="bg-[#668f45]/8 rounded-xl p-3 w-fit">
                          <div className="flex gap-1.5">
                            <div className="w-2 h-2 bg-[#668f45] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                            <div className="w-2 h-2 bg-[#668f45] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                            <div className="w-2 h-2 bg-[#668f45] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="bg-white px-4 py-3 border-t border-gray-200/60 flex-shrink-0">
                <div className="flex gap-1.5 bg-gray-50 border-2 border-gray-200 rounded-2xl p-1.5 mb-2 focus-within:border-[#668f45] focus-within:bg-white focus-within:shadow-lg transition-all">
                  <button
                    onClick={toggleVoiceInput}
                    title={isListening ? 'Stop listening' : 'Ask by voice'}
                    className={`flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center transition-all ${isListening ? 'bg-red-500 text-white animate-pulse' : 'text-gray-400 hover:text-[#668f45] hover:bg-[#668f45]/10'}`}
                  >
                    {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </button>
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask me any questions about ThirdEye Data... (Enter to send)"
                    disabled={loading}
                    maxLength={500}
                    className="flex-1 bg-transparent px-3 py-2 outline-none text-gray-900 font-medium placeholder-gray-400 text-sm min-w-0"
                  />
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button onClick={() => { if (input.trim()) { setIsMinimized(false); setIsMaximized(isEmbedded); sendMessage(); } }} disabled={loading || !input.trim()} className="bg-gradient-to-r from-[#7db34f] to-[#668f45] text-white px-5 py-2 rounded-xl font-extrabold hover:from-[#6ba043] hover:to-[#4a6632] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg text-sm">
                      Ask
                    </button>
                    <button onClick={() => { setIsMinimized(false); setIsMaximized(isEmbedded); sendMessage('Connect me with the Sales Team'); }} title="Talk to a ThirdEye Data expert" className="bg-[#28b9d2]/12 border-2 border-[#28b9d2] text-[#28b9d2] px-3 py-2 rounded-xl text-xs font-extrabold hover:bg-[#28b9d2] hover:text-white transition-all whitespace-nowrap">
                      Connect
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className={`text-[9px] font-medium ${input.length > 450 ? 'text-red-400' : 'text-gray-400'}`}>{input.length}/500</span>
                  <div className="text-center text-[9px] text-gray-400">
                    <span className="font-medium">Powered by ThirdEye Data</span>
                    <span className="mx-1.5 opacity-50">·</span>
                    <a href="https://thirdeyedata.ai/contact-us/" target="_blank" rel="noopener noreferrer" className="text-[#28b9d2] font-semibold hover:underline">Get Your Customised Version</a>
                  </div>
                  <span className="text-[9px] text-gray-300 select-none">↵ send</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
